// JACKPOT.EXE — three reels of Windows icons.
//
// Nothing to choose but the stake. The three reels carry the same sixteen-stop strip the contract
// carries, they blur while the round is in the air, and they stop left to right on stopOf() read
// back from the cabinet. Three globes is the jackpot and pays 100x.
//
// The reels are driven by one animation frame loop over a fractional strip position, not by CSS
// keyframes, so a reel lands on its exact stop rather than near it — what the payline shows is the
// symbol the contract says it is.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { CHIP, revealSlots } from 'lib/casino';
import { SLOTS_MAX_MULT_BPS, SLOTS_STRIP, slotsPayBps, slotsSymbolAt } from 'lib/casino-math';
import sfx from 'lib/sfx';
import { AutoPanel, useAuto } from './auto';
import { useBet, useStakeCap } from './state';
import { Blink, FONT, HouseRules, Marquee, Outcome, Panel, PlayButton, StakeBox, T, fmtChip, parseUnits, xpButton } from './ui';

const BPS = BigInt(10000);
const ALL_IN_BPS = BigInt(10500);
const REELS = 3;
const CELL = 56; // px per symbol on a reel
const STRIP_LEN = SLOTS_STRIP.length;
const SPIN_CELLS_PER_MS = 0.026;
const LAND_MS = 1500;
const STAGGER_MS = 430;

const NAMES = ['mine', 'recycle bin', 'folder', 'floppy disk', 'Clippy', 'globe'];

/**
 * The pay table, in the cabinet's own order. `text` and `pays` are what the plaque prints,
 * verbatim; `bps` is the same line in the contract's basis points, so the row that a settled
 * round lit up is found by comparing numbers rather than by re-parsing the label.
 */
const PAY_TABLE = [
  { text: 'three globes', pays: '100x', bps: BigInt(1000000), code: 5 },
  { text: 'three Clippys', pays: '50x', bps: BigInt(500000), code: 4 },
  { text: 'three disks', pays: '32x', bps: BigInt(320000), code: 3 },
  { text: 'three folders', pays: '15x', bps: BigInt(150000), code: 2 },
  { text: 'three bins', pays: '12.7x', bps: BigInt(127000), code: 1 },
  { text: 'two globes', pays: '10x', bps: BigInt(100000), code: 5 },
  { text: 'two Clippys', pays: '2.5x', bps: BigInt(25000), code: 4 },
  { text: 'two disks', pays: '2x', bps: BigInt(20000), code: 3 },
  { text: 'one globe', pays: '1.5x', bps: BigInt(15000), code: 5 },
];

const wrap = k => ((k % STRIP_LEN) + STRIP_LEN) % STRIP_LEN;

/** The six symbols, drawn rather than imported so they stay crisp at any reel size. */
function SlotIcon({ code }) {
  if (code === 0) {
    return (
      <svg viewBox="0 0 32 32" className="sl__icon" role="img" aria-label="mine">
        {[0, 45, 90, 135].map(a => (
          <rect key={a} x="15" y="4" width="2" height="24" fill="#111" transform={`rotate(${a} 16 16)`} />
        ))}
        <circle cx="16" cy="16" r="9" fill="#1b1b1b" />
        <circle cx="12.5" cy="12.5" r="2.6" fill="#fff" />
      </svg>
    );
  }
  if (code === 1) {
    return (
      <svg viewBox="0 0 32 32" className="sl__icon" role="img" aria-label="recycle bin">
        <path d="M8 10h16l-2 17H10z" fill="#b9c0c6" stroke="#5e6a72" strokeWidth="1.2" />
        <path d="M13 13l1 11M16 13v11M19 13l-1 11" stroke="#7d888f" strokeWidth="1.2" />
        <rect x="6" y="7" width="20" height="3.6" rx="1" fill="#d7dde1" stroke="#5e6a72" strokeWidth="1" />
        <path d="M16 3.5l3 2.5h-6z" fill="#2ea043" />
      </svg>
    );
  }
  if (code === 2) {
    return (
      <svg viewBox="0 0 32 32" className="sl__icon" role="img" aria-label="folder">
        <path d="M4 9h9l2.4 3H28v14H4z" fill="#f6c453" stroke="#a97b12" strokeWidth="1.2" />
        <path d="M4 14h24v12H4z" fill="#ffd86b" stroke="#a97b12" strokeWidth="1" />
      </svg>
    );
  }
  if (code === 3) {
    return (
      <svg viewBox="0 0 32 32" className="sl__icon" role="img" aria-label="floppy disk">
        <rect x="5" y="5" width="22" height="22" rx="1.5" fill="#23324a" stroke="#0d1626" strokeWidth="1.2" />
        <rect x="10" y="5" width="12" height="9" fill="#cdd6e2" />
        <rect x="17" y="6.5" width="3.5" height="6" fill="#5b6d85" />
        <rect x="9" y="17" width="14" height="10" fill="#e8eef6" stroke="#0d1626" strokeWidth="0.8" />
        <path d="M11 20h10M11 22.5h10" stroke="#8e9cb0" strokeWidth="1" />
      </svg>
    );
  }
  if (code === 4) {
    return (
      <svg viewBox="0 0 32 32" className="sl__icon" role="img" aria-label="Clippy">
        <path
          d="M21 9v13a5.5 5.5 0 01-11 0V8a3.4 3.4 0 016.8 0v13a1.6 1.6 0 01-3.2 0V10"
          fill="none"
          stroke="#b8c2cc"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <path
          d="M21 9v13a5.5 5.5 0 01-11 0V8a3.4 3.4 0 016.8 0v13a1.6 1.6 0 01-3.2 0V10"
          fill="none"
          stroke="#eef3f8"
          strokeWidth="1.1"
          strokeLinecap="round"
        />
        <circle cx="13.4" cy="12" r="2.4" fill="#fff" stroke="#37424d" strokeWidth="0.9" />
        <circle cx="18.6" cy="12" r="2.4" fill="#fff" stroke="#37424d" strokeWidth="0.9" />
        <circle cx="13.9" cy="12.4" r="1" fill="#111" />
        <circle cx="19.1" cy="12.4" r="1" fill="#111" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 32 32" className="sl__icon" role="img" aria-label="globe">
      <circle cx="16" cy="16" r="11" fill="#2f7fd4" stroke="#12406f" strokeWidth="1.2" />
      <path d="M5 16h22M16 5c4 4 4 18 0 22M16 5c-4 4-4 18 0 22" fill="none" stroke="#bfe0ff" strokeWidth="1" />
      <path d="M7.5 10.5c5 2.5 12 2.5 17 0M7.5 21.5c5-2.5 12-2.5 17 0" fill="none" stroke="#bfe0ff" strokeWidth="1" />
      <path d="M10 8a12 12 0 016-2" fill="none" stroke="#eaf5ff" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

/**
 * The three reels' fractional strip positions.
 * Free-runs while `spinning`, then eases each reel onto its stop, left to right.
 */
function useReelMotion(spinning, stops) {
  const [pos, setPos] = useState(() => [0, 0, 0]);
  const [landed, setLanded] = useState(() => [false, false, false]);
  const posRef = useRef([0, 0, 0]);

  useEffect(() => {
    if (!spinning || stops) return undefined;
    setLanded([false, false, false]);
    if (sfx.reelSpin) sfx.reelSpin(2200);
    let raf = null;
    let last = performance.now();
    const step = now => {
      const dt = now - last;
      last = now;
      posRef.current = posRef.current.map(p => p + dt * SPIN_CELLS_PER_MS);
      setPos(posRef.current.slice());
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => {
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }, [spinning, stops]);

  useEffect(() => {
    if (!stops) return undefined;
    const plans = stops.map(() => ({ from: null, to: null, done: false }));
    const started = performance.now();
    let last = started;
    let raf = null;
    const step = now => {
      const dt = now - last;
      last = now;
      const elapsed = now - started;
      const next = posRef.current.slice();
      for (let i = 0; i < REELS; i += 1) {
        const plan = plans[i];
        if (plan.done) continue;
        const t0 = i * STAGGER_MS;
        if (elapsed < t0) {
          next[i] += dt * SPIN_CELLS_PER_MS;
          continue;
        }
        if (plan.from === null) {
          plan.from = next[i];
          // Land forward, never backward, at least two more turns of the strip.
          const turns = Math.ceil((plan.from + STRIP_LEN * 2 - stops[i]) / STRIP_LEN);
          plan.to = stops[i] + STRIP_LEN * turns;
        }
        const t = Math.min(1, (elapsed - t0) / LAND_MS);
        const eased = 1 - Math.pow(1 - t, 4);
        next[i] = plan.from + (plan.to - plan.from) * eased;
        if (t >= 1) {
          plan.done = true;
          next[i] = plan.to;
          if (sfx.reelStop) sfx.reelStop();
          setLanded(prev => {
            const copy = prev.slice();
            copy[i] = true;
            return copy;
          });
        }
      }
      posRef.current = next;
      setPos(next);
      if (plans.every(p => p.done)) {
        raf = null;
        return;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => {
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }, [stops]);

  return { pos, landed };
}

function Reel({ position, blurred, hit }) {
  const base = Math.floor(position);
  const frac = position - base;
  const cells = [-1, 0, 1, 2, 3].map(k => ({ k, code: slotsSymbolAt(wrap(base + k)) }));
  return (
    <div className={`sl__reel${blurred ? ' blur' : ''}${hit ? ' hit' : ''}`}>
      <div className="sl__strip" style={{ transform: `translateY(${-frac * CELL}px)` }}>
        {cells.map(c => (
          <div key={c.k} className="sl__cell">
            <SlotIcon code={c.code} />
          </div>
        ))}
      </div>
      <div className="sl__glass" aria-hidden="true" />
    </div>
  );
}

export default function Slots({ casino }) {
  const [amount, setAmount] = useState('5');
  const [stops, setStops] = useState(null);
  const bet = useBet('slots', casino);

  const locked = bet.phase !== 'idle' && bet.phase !== 'done';
  const pending = bet.phase === 'approving' || bet.phase === 'placing' || bet.phase === 'waiting' || bet.phase === 'settling';
  // Keep turning through the gap between "settled" and "the reveal came back".
  const spinning = pending || (!!bet.result && stops === null);

  const allIn = useMemo(() => parseUnits(amount, CHIP.decimals), [amount]);
  const stake = (allIn * BPS) / ALL_IN_BPS;
  const jackpot = (stake * SLOTS_MAX_MULT_BPS) / BPS;

  // The house refuses a bet whose worst case it cannot cover, and the refusal is a revert with
  // no explanation. Read the live ceiling and never let the player submit past it.
  const stakeCap = useStakeCap('slots', Number(SLOTS_MAX_MULT_BPS), casino, amount, setAmount);
  const overCap = stakeCap !== null && stakeCap > BigInt(0) && allIn > stakeCap;
  const canPlay = casino.ready && allIn > BigInt(0) && !overCap;
  const auto = useAuto(bet, { allIn, args: [], canPlay, balance: casino.balance });
  const { pos, landed } = useReelMotion(spinning, stops);

  useEffect(() => {
    let alive = true;
    setStops(null);
    if (!bet.result) return undefined;
    revealSlots(bet.result.id, bet.result.randomness)
      .then(s => {
        if (alive) setStops(s.map(Number));
      })
      .catch(() => {
        // The receipt is the authority; leaving the reels turning beats showing symbols that lie.
        if (alive) setStops(null);
      });
    return () => {
      alive = false;
    };
  }, [bet.result]);

  const allLanded = landed.every(Boolean);
  const symbols = stops ? stops.map(slotsSymbolAt) : null;
  const payBps = symbols ? slotsPayBps(symbols[0], symbols[1], symbols[2]) : BigInt(0);
  const isJackpot = allLanded && payBps === SLOTS_MAX_MULT_BPS;

  // The jackpot flourish, once per round, on top of the win sound state.js already plays.
  const sungRef = useRef(null);
  useEffect(() => {
    if (!isJackpot || !bet.result) return;
    const id = String(bet.result.id);
    if (sungRef.current === id) return;
    sungRef.current = id;
    if (sfx.jackpot) sfx.jackpot();
  }, [isJackpot, bet.result]);

  return (
    <Div>
      <div className="sl__cabinet">
        <div className="sl__crown">
          <span className="sl__crownText">JACKPOT.EXE</span>
          <span className="sl__crownWin">
            <Blink>★ 100x ★</Blink>
          </span>
        </div>

        <div className="sl__window">
          {[0, 1, 2].map(i => (
            <Reel key={i} position={pos[i]} blurred={!landed[i] && (spinning || !!stops)} hit={allLanded && payBps > BigInt(0)} />
          ))}
          <div className="sl__payline" aria-hidden="true" />
        </div>

        <div className="sl__readout">
          {!allLanded
            ? spinning
              ? 'the reels are turning'
              : 'pull the handle'
            : payBps > BigInt(0)
              ? `${symbols.map(s => NAMES[s]).join(' · ')} — pays ${(Number(payBps) / 10000).toFixed(2)}x`
              : `${symbols.map(s => NAMES[s]).join(' · ')} — no line`}
        </div>

        <Marquee>
          ★ JACKPOT.EXE ★ THREE GLOBES PAYS 100x ★ THREE REELS, SIXTEEN STOPS EACH, ONE BEACON ROUND ★ THE STRIP IS THE CONTRACT&apos;S OWN ★ 18+ ★
        </Marquee>
      </div>

      <Panel>
        <div className="sl__table">
          <div className="sl__tableTitle">PAY TABLE</div>
          <div className="sl__tableRow">
            {PAY_TABLE.map((row, i) => (
              <span key={row.text} className={`sl__pay${allLanded && payBps === row.bps ? ' on' : ''}`}>
                {i === 0 ? null : <i className="sl__sep">·</i>}
                <SlotIcon code={row.code} />
                <b>{row.text}</b> <em>{row.pays}</em>
              </span>
            ))}
          </div>
        </div>

        <div className="sl__quote">
          <div>
            <span className="k">Pays</span>
            <span className="v">up to 100.00x</span>
          </div>
          <div>
            <span className="k">Stake</span>
            <span className="v">
              {fmtChip(stake)} {CHIP.symbol}
            </span>
          </div>
          <div>
            <span className="k">Jackpot</span>
            <span className="v">
              {fmtChip(jackpot)} {CHIP.symbol}
            </span>
          </div>
        </div>

        <StakeBox value={amount} onChange={setAmount} disabled={locked} />

        <PlayButton
          label="SPIN"
          onClick={() => bet.play(allIn, [])}
          disabled={!canPlay}
          phase={bet.phase}
          note={bet.note}
          countdown={bet.countdown}
        />

        <AutoPanel auto={auto} practice={casino.practice} />

        {bet.phase === 'stuck' ? (
          <div className="sl__warn">
            Your bet is placed and still open. <button onClick={bet.retrySettle}>Settle again</button>
          </div>
        ) : null}

        <Outcome result={bet.result} onAgain={bet.reset}>
          {symbols === null ? null : (
            <div className="sl__receipt">
              {symbols.map(s => NAMES[s]).join(' · ')}
              {isJackpot ? ' — JACKPOT' : ''}
            </div>
          )}
        </Outcome>

        <HouseRules
          practice={casino.practice}
        />
      </Panel>
    </Div>
  );
}

const Div = styled.div`
  .sl__cabinet {
    background: linear-gradient(180deg, #1d1206, #120b03);
    border: 4px solid #5a3a12;
    border-radius: 10px;
    box-shadow: inset 0 0 0 2px ${T.goldDeep}, 0 4px 8px rgba(0, 0, 0, 0.5);
    padding: 10px 12px 0;
    overflow: hidden;
  }
  .sl__crown {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding-bottom: 8px;
  }
  .sl__crownText {
    font: bold 20px ${FONT.serif};
    letter-spacing: 5px;
    color: ${T.gold};
    text-shadow: 2px 2px 0 ${T.red}, 0 0 16px rgba(255, 216, 107, 0.5);
  }
  .sl__crownWin {
    font: bold 11px ${FONT.web};
    color: #fff;
    background: ${T.red};
    padding: 2px 7px;
    border: 1px solid ${T.gold};
  }
  .sl__window {
    position: relative;
    display: flex;
    gap: 8px;
    justify-content: center;
    padding: 10px;
    background: linear-gradient(180deg, #0a3d22, #062a17);
    border: 3px double ${T.goldDeep};
  }
  .sl__reel {
    position: relative;
    width: ${CELL}px;
    height: ${CELL * 3}px;
    overflow: hidden;
    background: linear-gradient(180deg, #dfe6ee, #fff 25%, #fff 75%, #c9d2dc);
    border: 2px inset #9aa4ae;
  }
  .sl__reel.blur .sl__strip {
    filter: blur(2.2px);
  }
  .sl__reel.hit {
    box-shadow: 0 0 14px ${T.gold};
  }
  .sl__strip {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    will-change: transform;
  }
  .sl__cell {
    height: ${CELL}px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .sl__icon {
    width: 38px;
    height: 38px;
    display: block;
  }
  .sl__glass {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0) 28%, rgba(0, 0, 0, 0) 72%, rgba(0, 0, 0, 0.35));
  }
  .sl__payline {
    position: absolute;
    left: 6px;
    right: 6px;
    top: 50%;
    height: 2px;
    margin-top: -1px;
    background: ${T.red};
    box-shadow: 0 0 6px ${T.red};
    opacity: 0.75;
    pointer-events: none;
  }
  .sl__readout {
    text-align: center;
    font: 10px ${FONT.web};
    color: ${T.cream};
    padding: 8px 0;
  }

  .sl__table {
    margin-top: 12px;
    background: linear-gradient(180deg, #fff8d8, #ffd86b 55%, #d8a72a);
    border: 2px outset #f5d87a;
    padding: 7px 9px 9px;
  }
  .sl__tableTitle {
    font: bold 11px ${FONT.serif};
    letter-spacing: 5px;
    color: #3b2a00;
    text-align: center;
    margin-bottom: 5px;
  }
  .sl__tableRow {
    font: 11px ${FONT.web};
    color: #3b2a00;
    line-height: 1.9;
    text-align: center;
  }
  .sl__pay {
    white-space: nowrap;
  }
  .sl__pay .sl__icon {
    width: 15px;
    height: 15px;
    display: inline-block;
    vertical-align: -3px;
    margin-right: 3px;
  }
  .sl__pay b {
    font-weight: 400;
  }
  .sl__pay em {
    font-style: normal;
    font-weight: 700;
  }
  .sl__pay.on {
    background: #3b2a00;
    color: #ffd86b;
    border-radius: 2px;
    padding: 1px 3px;
  }
  .sl__sep {
    font-style: normal;
    padding: 0 6px;
    color: #8a6a10;
  }

  .sl__quote {
    display: flex;
    gap: 6px;
    margin-top: 10px;
    flex-wrap: wrap;
  }
  .sl__quote > div {
    flex: 1;
    min-width: 92px;
    background: #fff;
    border: 1px solid #7f9db9;
    box-shadow: inset 1px 1px 0 #d6dde5;
    padding: 3px 6px;
  }
  .sl__quote .k {
    display: block;
    font-size: 9px;
    color: #555;
  }
  .sl__quote .v {
    display: block;
    font-size: 12px;
    font-weight: 700;
    color: #000;
    margin-top: 1px;
  }
  .sl__warn {
    margin-top: 8px;
    font: 10px ${FONT.ui};
    color: #8a0000;
  }
  .sl__warn button {
    ${xpButton};
    min-width: 0;
    min-height: 19px;
    padding: 0 8px;
    margin-left: 6px;
  }
  .sl__receipt {
    font: 11px ${FONT.ui};
    margin-bottom: 4px;
  }
`;
